import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import type { Communication } from '@medplum/fhirtypes';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { isMessagingParticipantProfile } from '../services/messagingIdentity';
import {
  alignThreadRootsWithLatestDirectMessage,
  fetchThreadsForUser,
} from '../services/messagingFhir';
import { useMessagingRealtime } from './useMessagingRealtime';
import { createThreadRefreshCoalescer } from './useMessagingRealtime';
import { UNREAD_STATE_CHANGED_EVENT } from './useUnreadThreadCount';

const isDev = import.meta.env.DEV;

/**
 * Loads the thread list for the current user.
 *
 * Realtime: shared MessagingRealtimeProvider (one broad WebSocket per tab).
 * Each message event triggers a debounced full thread refetch so sidebar previews,
 * sort order, and unread dots stay in sync with the server (including thread-root updates).
 */
export function useMessagingThreads(extraThreads?: Communication[]): {
  threads: Communication[];
  loading: boolean;
  refetch: () => void;
} {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const location = useLocation();
  const [threads, setThreads] = useState<Communication[]>([]);
  const [loading, setLoading] = useState(true);

  const userRef = useMemo(() => {
    if (!profile?.id || !isMessagingParticipantProfile(profile)) return undefined;
    return `${profile.resourceType}/${profile.id}`;
  }, [profile]);

  const loadingRef = useRef(false);
  const pendingReloadRef = useRef(false);

  const fetchThreadsFull = useCallback(async () => {
    if (!userRef) {
      setThreads([]);
      setLoading(false);
      return;
    }

    if (loadingRef.current) {
      pendingReloadRef.current = true;
      return;
    }
    loadingRef.current = true;
    setLoading(true);
    if (isDev) {
      console.log('[Messaging][Sidebar] Full thread refetch triggered');
    }
    try {
      medplum.invalidateSearches('Communication');
      const result = await fetchThreadsForUser(medplum, userRef);
      const aligned = await alignThreadRootsWithLatestDirectMessage(medplum, result);
      setThreads(aligned);
    } catch (err) {
      console.error('[Messaging] Failed to load threads:', err);
    } finally {
      setLoading(false);
      loadingRef.current = false;
      if (pendingReloadRef.current) {
        pendingReloadRef.current = false;
        void fetchThreadsFull();
      }
    }
  }, [medplum, userRef]);

  const fetchThreadsFullRef = useRef(fetchThreadsFull);
  fetchThreadsFullRef.current = fetchThreadsFull;

  const coalescerRef = useRef<ReturnType<typeof createThreadRefreshCoalescer>>();
  if (!coalescerRef.current) {
    coalescerRef.current = createThreadRefreshCoalescer(() => {
      void fetchThreadsFullRef.current();
    }, 300);
  }

  const { onMessage, onReconnect } = useMessagingRealtime();

  useEffect(() => {
    const unsub = onMessage((comm: Communication) => {
      if (isDev) {
        console.log('[Messaging][Sidebar] Realtime message → debounced refetch', comm.id);
      }
      coalescerRef.current?.enqueue(null);
    });
    return unsub;
  }, [onMessage]);

  useEffect(() => {
    const unsub = onReconnect(() => {
      if (isDev) {
        console.log('[Messaging][Sidebar] Reconnect → full refetch');
      }
      void fetchThreadsFullRef.current();
    });
    return unsub;
  }, [onReconnect]);

  useEffect(() => {
    void fetchThreadsFull();
  }, [fetchThreadsFull, location.pathname]);

  useEffect(() => {
    const handler = (): void => {
      void fetchThreadsFull();
    };
    window.addEventListener(UNREAD_STATE_CHANGED_EVENT, handler);
    return () => window.removeEventListener(UNREAD_STATE_CHANGED_EVENT, handler);
  }, [fetchThreadsFull]);

  useEffect(() => {
    const refresh = (): void => {
      void fetchThreadsFull();
    };
    const onVis = (): void => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [fetchThreadsFull]);

  useEffect(() => {
    return () => {
      coalescerRef.current?.cancel();
    };
  }, []);

  const merged = useMemo(() => {
    if (!extraThreads?.length) return threads;
    const serverIds = new Set(threads.map((t) => t.id).filter(Boolean));
    const pendingOnly = extraThreads.filter((t) => t.id && !serverIds.has(t.id));
    return [...pendingOnly, ...threads];
  }, [threads, extraThreads]);

  return { threads: merged, loading, refetch: fetchThreadsFull };
}
